/**
 * ScribeAudioEngine
 * Captures microphone audio for ambient scribe sessions.
 *
 * Uses MediaStreamTrackProcessor (Insertable Streams API) to read raw PCM
 * directly from the mic track — bypasses Web Audio API entirely.
 * Falls back to ScriptProcessorNode if MediaStreamTrackProcessor is unavailable.
 *
 * Output: 16kHz, 16-bit, mono LPCM, base64 encoded (matches HealthScribe requirements).
 */

const TARGET_SAMPLE_RATE = 16000;

export class ScribeAudioEngine {
  constructor() {
    this.stream = null;
    this.isCapturing = false;
    this.onChunk = null;
    this.onSource = null;   // called once with what the mic actually delivers (GH-184 diagnosis)
    this.audioSource = null;
    this._abortController = null;
    // For ScriptProcessor fallback
    this._audioContext = null;
    this._source = null;
    this._scriptProcessor = null;
  }

  /**
   * Pre-initialize during user gesture (button click).
   * For MediaStreamTrackProcessor, no warmup is needed — just a no-op.
   * For ScriptProcessor fallback, creates AudioContext.
   */
  async warmup() {
    // eslint-disable-next-line no-undef
    if (typeof MediaStreamTrackProcessor !== 'undefined') {
      console.log('[ScribeAudio] MediaStreamTrackProcessor available — no warmup needed');
      return;
    }

    // Fallback: create AudioContext for ScriptProcessorNode
    if (!this._audioContext || this._audioContext.state === 'closed') {
      this._audioContext = new (window.AudioContext || window.webkitAudioContext)({
        sampleRate: TARGET_SAMPLE_RATE
      });
    }
    if (this._audioContext.state === 'suspended') {
      await this._audioContext.resume();
    }
    console.log('[ScribeAudio] Warmup: AudioContext for fallback, state:', this._audioContext.state);
  }

  /**
   * Start capturing audio from the microphone
   */
  async startCapture(onChunk, onSource = null) {
    if (this.isCapturing) {
      console.warn('[ScribeAudio] Already capturing');
      return;
    }
    this.onChunk = onChunk;
    this.onSource = onSource;

    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        channelCount: 1,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true
      }
    });

    const track = this.stream.getAudioTracks()[0];
    const settings = track.getSettings();
    console.log('[ScribeAudio] Mic:', track.label, 'rate:', settings.sampleRate, 'channels:', settings.channelCount);
    this.audioSource = { label: track.label, settingsSampleRate: settings.sampleRate || null, channels: settings.channelCount || null, path: null, frameSampleRate: null, frameFormat: null };

    this.isCapturing = true;

    // eslint-disable-next-line no-undef
    if (typeof MediaStreamTrackProcessor !== 'undefined') {
      console.log('[ScribeAudio] Using MediaStreamTrackProcessor (bypasses Web Audio API)');
      await this._startWithTrackProcessor(track, settings.sampleRate || 48000);
    } else {
      console.log('[ScribeAudio] Fallback: using ScriptProcessorNode');
      await this._startWithScriptProcessor();
    }
  }

  /**
   * Primary: MediaStreamTrackProcessor reads raw PCM from mic track.
   * No Web Audio API involved.
   */
  async _startWithTrackProcessor(track, settingsSampleRate) {
    // eslint-disable-next-line no-undef
    const processor = new MediaStreamTrackProcessor({ track });
    const reader = processor.readable.getReader();

    this._abortController = new AbortController();

    let chunkNum = 0;
    let buffer = new Float32Array(0);
    // The rate the resampler uses comes from the frames themselves, not from
    // track.getSettings() — GH-184 (QA 2026-09-18): the live transcript was
    // word-salad ("the car fetus") while the post-stream note was fine, which
    // is what feeding 44.1 kHz frames through a 48 kHz assumption sounds like.
    // Until the first frame arrives, size the 100 ms chunk from the setting.
    let micSampleRate = settingsSampleRate;
    let targetSamples = Math.floor(micSampleRate * 0.1);

    console.log('[ScribeAudio] Reading audio frames. Settings rate:', settingsSampleRate, 'initial chunk:', targetSamples, 'samples');

    // Read loop runs async
    const readLoop = async () => {
      try {
        while (this.isCapturing) {
          const { value: audioData, done } = await reader.read();
          if (done || !this.isCapturing) break;

          if (audioData.sampleRate && audioData.sampleRate !== micSampleRate) {
            if (buffer.length) { // flush what was captured at the previous rate
              const flushed = resample(buffer, micSampleRate, TARGET_SAMPLE_RATE);
              if (flushed.length && this.onChunk) this.onChunk(float32ToBase64PCM(flushed));
              buffer = new Float32Array(0);
            }
            micSampleRate = audioData.sampleRate;
            targetSamples = Math.floor(micSampleRate * 0.1);
            console.log(`[ScribeAudio] Frame rate is ${micSampleRate} Hz (settings said ${settingsSampleRate}); chunk ${targetSamples} samples, format ${audioData.format}`);
          }
          if (!this.audioSource.path) {
            this.audioSource = { ...this.audioSource, path: 'trackProcessor', frameSampleRate: audioData.sampleRate, frameFormat: audioData.format, frameChannels: audioData.numberOfChannels };
            if (this.onSource) this.onSource(this.audioSource);
          }

          // AudioData → Float32Array (plane 0 = first channel; ask for f32
          // explicitly so an s16/u8 mic format is converted, not reinterpreted)
          const pcm = audioDataToFloat32(audioData);
          audioData.close();

          // Accumulate into buffer
          const newBuf = new Float32Array(buffer.length + pcm.length);
          newBuf.set(buffer);
          newBuf.set(pcm, buffer.length);
          buffer = newBuf;

          // Send chunks when we have enough
          while (buffer.length >= targetSamples) {
            const chunk = buffer.slice(0, targetSamples);
            buffer = buffer.slice(targetSamples);

            // Resample to 16kHz (box filter — no aliasing from bare decimation)
            const downsampled = resample(chunk, micSampleRate, TARGET_SAMPLE_RATE);
            const base64 = float32ToBase64PCM(downsampled);

            chunkNum++;
            if (chunkNum <= 5 || chunkNum % 100 === 0) {
              console.log(`[ScribeAudio] Chunk #${chunkNum}: ${chunk.length}@${micSampleRate}→${downsampled.length}@16k→${base64.length}b64`);
            }

            if (this.onChunk) this.onChunk(base64);
          }
        }
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('[ScribeAudio] Read loop error:', err);
        }
      } finally {
        reader.releaseLock();
        console.log('[ScribeAudio] Read loop ended. Chunks sent:', chunkNum);
      }
    };

    readLoop();
  }

  /**
   * Fallback: ScriptProcessorNode (deprecated but widely supported)
   */
  async _startWithScriptProcessor() {
    if (!this._audioContext || this._audioContext.state === 'closed') {
      this._audioContext = new (window.AudioContext || window.webkitAudioContext)({
        sampleRate: TARGET_SAMPLE_RATE
      });
    }
    if (this._audioContext.state === 'suspended') {
      await this._audioContext.resume();
    }

    this._source = this._audioContext.createMediaStreamSource(this.stream);
    this._scriptProcessor = this._audioContext.createScriptProcessor(2048, 1, 1);
    this.audioSource = { ...(this.audioSource || {}), path: 'scriptProcessor', frameSampleRate: this._audioContext.sampleRate, frameFormat: 'f32 (AudioContext)' };
    if (this.onSource) this.onSource(this.audioSource);

    let chunkNum = 0;
    this._scriptProcessor.onaudioprocess = (event) => {
      if (!this.isCapturing || !this.onChunk) return;

      const float32 = event.inputBuffer.getChannelData(0);
      const base64 = float32ToBase64PCM(float32);

      chunkNum++;
      if (chunkNum <= 5 || chunkNum % 100 === 0) {
        console.log(`[ScribeAudio] Chunk #${chunkNum}: ${float32.length} samples → ${base64.length} b64`);
      }

      this.onChunk(base64);
    };

    this._source.connect(this._scriptProcessor);
    this._scriptProcessor.connect(this._audioContext.destination);

    console.log('[ScribeAudio] ScriptProcessor capture at', this._audioContext.sampleRate, 'Hz');
  }

  stopCapture() {
    this.isCapturing = false;
    this.onChunk = null;

    // Abort track processor read loop
    if (this._abortController) {
      this._abortController.abort();
      this._abortController = null;
    }

    // Cleanup ScriptProcessor fallback
    if (this._scriptProcessor) {
      this._scriptProcessor.disconnect();
      this._scriptProcessor.onaudioprocess = null;
      this._scriptProcessor = null;
    }
    if (this._source) {
      this._source.disconnect();
      this._source = null;
    }

    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
      this.stream = null;
    }

    console.log('[ScribeAudio] Capture stopped');
  }

  destroy() {
    this.stopCapture();
    if (this._audioContext && this._audioContext.state !== 'closed') {
      this._audioContext.close();
      this._audioContext = null;
    }
  }
}

// ── Helpers ──

/**
 * Copy one channel of an AudioData frame as Float32. Chrome delivers mic
 * frames as f32-planar; asking copyTo for f32-planar makes other formats
 * (s16, u8, interleaved) convert instead of being read as garbage. Older
 * browsers without the `format` option fall back to a plain copy.
 */
export function audioDataToFloat32(audioData) {
  const pcm = new Float32Array(audioData.numberOfFrames);
  try {
    audioData.copyTo(pcm, { planeIndex: 0, format: 'f32-planar' });
  } catch {
    audioData.copyTo(pcm, { planeIndex: 0 });
  }
  return pcm;
}

/**
 * Rate conversion by box filter: each output sample is the mean of the input
 * samples it spans. Exact for integer ratios (48k→16k), and for 44.1k→16k it
 * averages ~2.76 samples per output — enough anti-aliasing for speech and no
 * pitch shift. The previous decimation (take every ratio-th sample) aliased
 * everything above 8 kHz back into the band.
 */
export function resample(input, fromRate, toRate) {
  if (!fromRate || !toRate || fromRate === toRate) return input;
  const ratio = fromRate / toRate;
  const outLen = Math.round(input.length / ratio);
  const out = new Float32Array(outLen);
  for (let i = 0; i < outLen; i++) {
    const start = i * ratio;
    const end = Math.min(input.length, (i + 1) * ratio);
    let sum = 0, n = 0;
    for (let j = Math.floor(start); j < end; j++) { sum += input[j]; n++; }
    out[i] = n ? sum / n : 0;
  }
  return out;
}

function float32ToBase64PCM(float32Array) {
  const int16 = new Int16Array(float32Array.length);
  for (let i = 0; i < float32Array.length; i++) {
    const s = Math.max(-1, Math.min(1, float32Array[i]));
    int16[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
  }
  const bytes = new Uint8Array(int16.buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}
